import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, AlertTriangle, Clock, ChevronRight, CalendarClock } from 'lucide-react';
import i18n from '../i18n';
import {
  getCodaLavoro, getConferimentiProgrammati, getThroughput, getSaturazioneAree, getKpiFatturato,
  type CodaVoce, type ConfProgrammato, type ThroughputResponse, type SaturazioneArea, type Granularita,
  type KpiFatturatoResponse, type KpiBlocco, type SegmentoKg,
} from '../api/dashboard';
import { getDiagnosticaLedger } from '../api/diagnostica';
import { useAuthStore } from '../store/authStore';
import { canViewDiagnostica, canViewKpiFatturato } from '../lib/constants';
import StackedBarChart, { type BarraImpilata } from '../components/dashboard/StackedBarChart';
import DrillDownTipologieModal from '../components/dashboard/DrillDownTipologieModal';
import MagazzinoVenditeSection from '../components/dashboard/MagazzinoVenditeSection';
import { SCADENZE_COMPLIANCE, prossimaOccorrenza } from '../lib/scadenzeCompliance';
import { ROUTE_BY_CHIAVE } from '../lib/codaLavoroRoutes';
import { fmtKg } from '../lib/saldo';

// Lingua fissata al build: array risolto a import-time (DashboardPage è lazy,
// i18n già inizializzato in main).
const MESI_SHORT = i18n.t('dashboard.mesi_short', { returnObjects: true }) as string[];

// Voci urgenti in cima + colore.
const URGENZA: Record<string, 'rosso' | 'ambra'> = {
  conferimenti_in_ritardo: 'rosso',
  omologhe_in_scadenza: 'ambra',
};

function formatPeriodo(periodo: string, gran: Granularita): string {
  if (gran === 'anno') return periodo;
  if (gran === 'settimana') return periodo.replace(/^\d+-W/, 'S'); // 2026-W23 → S23
  if (gran === 'mese') {
    const m = Number(periodo.split('-')[1]);
    return MESI_SHORT[m - 1] ?? periodo;
  }
  // giorno: 2026-06-09 → "9 giu"
  const [, mm, dd] = periodo.split('-');
  return `${Number(dd)} ${MESI_SHORT[Number(mm) - 1] ?? ''}`.trim();
}

// Wrapper locale con l'unita', come `kg()` in DiagnosticaLedgerSection: il
// formato resta quello di `fmtKg`, qui si aggiunge solo il suffisso. Zero
// decimali perche' su una KPI i centesimi sono rumore — vedi `decimaliMax`.
function kg(n: number | null): string {
  return n == null ? '—' : `${fmtKg(n, 0)} kg`;
}
// Gli importi dei KPI sono EURO: zero decimali (su una KPI i centesimi sono
// rumore, come per i kg) e `useGrouping: 'always'` ESPLICITO — senza, CLDR
// it-IT applica `minimumGroupingDigits: 2` e 1500 resta "1500" mentre 20000
// diventa "20.000". E' la stessa ragione per cui `fmtKg` esiste in quella
// forma; qui non si puo' riusare perche' non sono kg.
function eur(n: number): string {
  return n.toLocaleString('it-IT', { maximumFractionDigits: 0, useGrouping: 'always' });
}
/** Pesi dei grafici: `fmtKg` e basta, senza unita' (la mette il grafico). */
function kgChart(n: number): string {
  return fmtKg(n, 0);
}
function fmtData(iso: string | null): string {
  return iso ? new Date(iso + 'T00:00:00').toLocaleDateString('it-IT') : '—';
}

// `label` = chiave i18n (tradotta al render).
const GRANS: { value: Granularita; label: string }[] = [
  { value: 'giorno', label: 'dashboard.granularita.giorno' }, { value: 'settimana', label: 'dashboard.granularita.settimana' },
  { value: 'mese', label: 'dashboard.granularita.mese' }, { value: 'anno', label: 'dashboard.granularita.anno' },
];

function satColor(pct: number): string {
  if (pct >= 80) return '#ef4444';
  if (pct >= 50) return '#f59e0b';
  return '#84a07c';
}

function WidgetError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="px-4 py-6 text-center" data-testid="widget-error">
      <p className="text-sm text-accent-danger mb-2 flex items-center justify-center gap-1.5">
        <AlertTriangle size={15} /> {t('dashboard.widget.impossibile_caricare')}
      </p>
      <button type="button" onClick={onRetry} className="text-xs text-sage-dark hover:underline">{t('common.azioni.riprova')}</button>
    </div>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Ogni widget distingue "nessun dato" da "errore di caricamento" (no degrado silenzioso).
  const [coda, setCoda] = useState<CodaVoce[]>([]);
  const [codaErr, setCodaErr] = useState(false);
  const [prog, setProg] = useState<ConfProgrammato[]>([]);
  const [progErr, setProgErr] = useState(false);
  const [throughput, setThroughput] = useState<ThroughputResponse | null>(null);
  const [throughputErr, setThroughputErr] = useState(false);
  const [granularita, setGranularita] = useState<Granularita>('mese');
  const [aree, setAree] = useState<SaturazioneArea[]>([]);
  const [areeErr, setAreeErr] = useState(false);
  // #116-bis — anomalie del ledger. Nessuno stato di errore: se la chiamata
  // fallisce il contatore semplicemente non compare, non si mette un widget
  // rotto in cima alla Dashboard di tutti.
  const [anomalie, setAnomalie] = useState(0);
  const ruolo = useAuthStore((s) => s.user?.role);
  const canDiagnostica = canViewDiagnostica(ruolo);
  // Gate COSMETICO: l'autorita' resta il backend, che risponde 403. Serve a non
  // chiamare un endpoint vietato e a non mostrare una sezione vuota a chi non
  // deve vederla — e, se la chiamata parte lo stesso (ruolo cambiato lato
  // server), il 403 NON diventa un widget in errore: la sezione sparisce.
  const canKpi = canViewKpiFatturato(ruolo);
  const [kpi, setKpi] = useState<KpiFatturatoResponse | null>(null);
  const [kpiVietato, setKpiVietato] = useState(false);
  // Drill-down di UNA fetta della barra Produzioni. Il `periodo` conservato e'
  // la label GREZZA del backend, non quella resa a video: e' la chiave con cui
  // il dettaglio ritrova lo stesso bucket.
  const [drill, setDrill] = useState<
    { periodo: string; materiale: string; etichetta: string; kg: number } | null>(null);

  const loadCoda = useCallback(() => {
    void (async () => {
      try { setCoda(await getCodaLavoro()); setCodaErr(false); }
      catch (e) { console.error('dashboard/coda-lavoro', e); setCodaErr(true); }
    })();
  }, []);
  const loadProg = useCallback(() => {
    void (async () => {
      try { setProg(await getConferimentiProgrammati(14)); setProgErr(false); }
      catch (e) { console.error('dashboard/conferimenti-programmati', e); setProgErr(true); }
    })();
  }, []);
  const loadAree = useCallback(() => {
    void (async () => {
      try { setAree(await getSaturazioneAree()); setAreeErr(false); }
      catch (e) { console.error('dashboard/saturazione-aree', e); setAreeErr(true); }
    })();
  }, []);
  const loadThroughput = useCallback((g: Granularita) => {
    void (async () => {
      try { setThroughput(await getThroughput(g)); setThroughputErr(false); }
      catch (e) { console.error('dashboard/throughput', e); setThroughput(null); setThroughputErr(true); }
    })();
  }, []);

  // Il totale arriva già calcolato dal backend (controlli 1+2, saldi negativi
  // esclusi): non si ricontano le righe qui, o contatore e pagina divergono.
  useEffect(() => {
    if (!canDiagnostica) return;
    void (async () => {
      try { setAnomalie((await getDiagnosticaLedger()).totale_anomalie); }
      catch (e) { console.error('diagnostica/ledger', e); }
    })();
  }, [canDiagnostica]);

  const loadKpi = useCallback((g: Granularita) => {
    void (async () => {
      try { setKpi(await getKpiFatturato(g)); setKpiVietato(false); }
      catch (e) {
        const stato = (e as { response?: { status?: number } })?.response?.status;
        // Un 403 non e' un guasto: e' la risposta giusta per quel ruolo, e
        // mostrarlo come errore direbbe all'utente che qualcosa e' rotto.
        if (stato === 403) { setKpiVietato(true); setKpi(null); return; }
        console.error('dashboard/kpi-fatturato', e);
        setKpi(null);
      }
    })();
  }, []);

  useEffect(() => { loadCoda(); loadProg(); loadAree(); }, [loadCoda, loadProg, loadAree]);
  useEffect(() => { loadThroughput(granularita); }, [granularita, loadThroughput]);
  // Stesso selettore di periodo dell'Andamento: e' la stessa domanda posta su
  // due unita' di misura, e due selettori separati si disallineerebbero.
  useEffect(() => { if (canKpi) loadKpi(granularita); }, [canKpi, granularita, loadKpi]);

  // Coda: derive conferimenti_in_ritardo dal conteggio se assente nell'endpoint.
  const codaCompleta: CodaVoce[] = (() => {
    const voci = [...coda];
    if (!voci.some((v) => v.chiave === 'conferimenti_in_ritardo')) {
      const n = prog.filter((p) => p.in_ritardo).length;
      voci.push({ chiave: 'conferimenti_in_ritardo', label: t('dashboard.coda.conferimenti_in_ritardo'), count: n, ruolo: 'AMM' });
    }
    return voci;
  })();
  // Urgenti in cima.
  const codaOrdinata = [...codaCompleta].sort((a, b) => {
    const ua = URGENZA[a.chiave] ? 0 : 1;
    const ub = URGENZA[b.chiave] ? 0 : 1;
    return ua - ub;
  });

  const serie = throughput?.serie ?? [];
  const datasets = [
    { title: t('dashboard.throughput.ingresso'), color: '#84a07c', key: 'ingresso_kg' as const, seg: 'segmenti_ingresso' as const },
    { title: t('dashboard.throughput.scarichi'), color: '#7a8db0', key: 'scarichi_kg' as const, seg: 'segmenti_scarichi' as const },
    { title: t('dashboard.throughput.produzioni'), color: '#c4956a', key: 'produzioni_kg' as const, seg: 'segmenti_produzioni' as const },
    { title: t('dashboard.throughput.uscite'), color: '#a07c9b', key: 'uscite_kg' as const, seg: 'segmenti_uscite' as const },
  ];

  // Il TOTALE della barra resta quello della serie, mai la somma dei segmenti:
  // i segmenti spiegano il totale e non lo correggono, e se divergessero il
  // grafico deve dire la stessa cifra della testata.
  const barre = (key: typeof datasets[number]['key'], seg: typeof datasets[number]['seg']): BarraImpilata[] =>
    serie.map((s) => ({
      label: formatPeriodo(s.periodo, granularita),
      periodo: s.periodo,
      totale: s[key],
      segmenti: ((s[seg] ?? []) as SegmentoKg[]).map((g) => ({ chiave: g.chiave, etichetta: g.etichetta, valore: g.kg })),
    }));
  const blocchiKpi: { titolo: string; color: string; blocco: KpiBlocco; nota?: string; testid: string }[] = kpi ? [
    { titolo: t('dashboard.kpi.attive_servizio'), color: '#84a07c', blocco: kpi.attive_servizio, testid: 'kpi-attive-servizio' },
    { titolo: t('dashboard.kpi.attive_vendita'), color: '#7a8db0', blocco: kpi.attive_vendita, testid: 'kpi-attive-vendita' },
    // La ripartizione delle passive fra categorie e' PROPORZIONALE AL PESO:
    // `fatture_passive` ha un importo solo e nessun dettaglio di riga. E' una
    // stima dichiarata, e va dichiarata anche a video.
    { titolo: t('dashboard.kpi.passive'), color: '#c4956a', blocco: kpi.passive, nota: t('dashboard.kpi.nota_stima'), testid: 'kpi-passive' },
  ] : [];

  const oggi = new Date();
  const scadenze = SCADENZE_COMPLIANCE
    .map((s) => ({ ...s, data: prossimaOccorrenza(s, oggi) }))
    .sort((a, b) => a.data.getTime() - b.data.getTime());

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-2">
        <LayoutDashboard size={20} className="text-sage" />
        <h1 className="text-xl font-semibold text-ink-dark">{t('common.nav.dashboard')}</h1>
      </div>

      {/* 0) Anomalie ledger (#116-bis). Compare SOLO se ce ne sono: a zero non
             si mostra nulla — un badge verde permanente si smette di leggere,
             ed è così che il disallineamento è passato inosservato per settimane. */}
      {canDiagnostica && anomalie > 0 && (
        <button
          type="button"
          onClick={() => navigate('/settings?tab=diagnostica')}
          data-testid="dashboard-anomalie-ledger"
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left bg-accent-danger-bg border border-[#FECACA] rounded-xl hover:opacity-90 transition-opacity"
        >
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle size={16} className="text-accent-danger flex-shrink-0" />
            <span className="text-sm font-medium text-accent-danger">{t('dashboard.anomalie_ledger.titolo')}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm font-semibold tabular-nums text-accent-danger">{anomalie}</span>
            <ChevronRight size={15} className="text-accent-danger" />
          </div>
        </button>
      )}

      {/* 1) Coda di lavoro */}
      <section>
        <h2 className="text-sm font-semibold text-ink-soft uppercase tracking-wide mb-2">{t('dashboard.coda.titolo')}</h2>
        <div className="bg-white border border-stone-mid rounded-xl divide-y divide-stone-mid">
          {codaErr ? (
            <WidgetError onRetry={loadCoda} />
          ) : codaOrdinata.length === 0 ? (
            <div className="px-4 py-6 text-sm text-ink-soft text-center">{t('dashboard.coda.vuota')}</div>
          ) : codaOrdinata.map((v) => {
            const urg = v.count > 0 ? URGENZA[v.chiave] : undefined;
            const route = ROUTE_BY_CHIAVE[v.chiave];
            return (
              <button key={v.chiave} type="button" disabled={!route}
                onClick={() => route && navigate(route)}
                data-testid={`coda-${v.chiave}`}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${route ? 'hover:bg-stone-bg cursor-pointer' : 'cursor-default'}`}>
                <div className="flex items-center gap-2 min-w-0">
                  {urg === 'rosso' && <AlertTriangle size={15} className="text-red-600 flex-shrink-0" />}
                  {urg === 'ambra' && <Clock size={15} className="text-amber-500 flex-shrink-0" />}
                  <span className={`text-sm ${urg === 'rosso' ? 'text-red-700 font-medium' : urg === 'ambra' ? 'text-amber-700 font-medium' : 'text-ink-dark'}`}>{v.label}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-bg text-ink-soft font-medium">{v.ruolo}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-sm font-semibold tabular-nums ${urg === 'rosso' ? 'text-red-700' : urg === 'ambra' ? 'text-amber-700' : 'text-ink-dark'}`}>{v.count}</span>
                  {route && <ChevronRight size={15} className="text-ink-faint" />}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* 2) Conferimenti programmati */}
      <section>
        <h2 className="text-sm font-semibold text-ink-soft uppercase tracking-wide mb-2">{t('dashboard.programmati.titolo')}</h2>
        <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
          {progErr ? (
            <WidgetError onRetry={loadProg} />
          ) : prog.length === 0 ? (
            <div className="px-4 py-6 text-sm text-ink-soft text-center">{t('dashboard.programmati.vuota')}</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-bg">
                <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                  <th className="px-3 py-2">{t('dashboard.programmati.col_data')}</th><th className="px-3 py-2">{t('dashboard.programmati.col_ordine')}</th>
                  <th className="px-3 py-2">{t('dashboard.programmati.col_cliente')}</th><th className="px-3 py-2 text-right">{t('dashboard.programmati.col_peso')}</th>
                  <th className="px-3 py-2">{t('dashboard.programmati.col_area')}</th><th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {prog.map((p) => (
                  <tr key={p.conferimento_id} className="border-t border-stone-mid/60 hover:bg-stone-bg/50 cursor-pointer"
                    onClick={() => navigate(`/conferimenti/${p.conferimento_id}`)} data-testid={`prog-${p.conferimento_id}`}>
                    <td className="px-3 py-2">{fmtData(p.data_prevista)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.codice_ordine}</td>
                    <td className="px-3 py-2">{p.customer_ragione_sociale}</td>
                    <td className="px-3 py-2 text-right">{kg(p.peso_stimato_kg)}</td>
                    <td className="px-3 py-2">{p.area_codice ?? '—'}</td>
                    <td className="px-3 py-2">
                      {p.in_ritardo && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">{t('dashboard.programmati.in_ritardo')}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* 3) Andamento */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-ink-soft uppercase tracking-wide">{t('dashboard.throughput.titolo')}</h2>
          <div className="flex gap-1">
            {GRANS.map((g) => (
              <button key={g.value} type="button" onClick={() => setGranularita(g.value)}
                data-testid={`gran-${g.value}`}
                className={`px-2.5 py-1 text-xs font-medium rounded border transition-colors ${granularita === g.value ? 'border-sage bg-sage-light text-sage-dark' : 'border-stone-border text-ink-soft hover:bg-stone-bg'}`}>
                {t(g.label)}
              </button>
            ))}
          </div>
        </div>
        {throughputErr ? (
          <div className="bg-white border border-stone-mid rounded-xl">
            <WidgetError onRetry={() => loadThroughput(granularita)} />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {datasets.map((ds) => (
              <StackedBarChart key={ds.key} title={ds.title} color={ds.color}
                emptyLabel={t('dashboard.throughput.nessun_dato')} format={kgChart}
                data={barre(ds.key, ds.seg)}
                // Solo le PRODUZIONI hanno un dettaglio per tipologia: sulle
                // altre serie la fetta resta inerte invece di diventare un
                // bottone che non porta da nessuna parte.
                onSegmentClick={ds.key === 'produzioni_kg'
                  ? (barra, segmento) => setDrill({
                      periodo: barra.periodo, materiale: segmento.chiave,
                      etichetta: segmento.etichetta, kg: segmento.valore })
                  : undefined}
                segmentAriaLabel={ds.key === 'produzioni_kg'
                  ? (barra, segmento) => t('dashboard.drilldown.aria', {
                      materiale: segmento.etichetta, periodo: barra.label })
                  : undefined} />
            ))}
          </div>
        )}
      </section>

      {/* 3-bis) KPI amministrazione (V72). Il periodo e' quello dell'Andamento,
             scelto col selettore qui sopra. Niente stato d'errore: a 403 la
             sezione non c'e', ed e' la risposta giusta, non un guasto. */}
      {canKpi && !kpiVietato && kpi && (
        <section data-testid="kpi-amministrazione">
          <h2 className="text-sm font-semibold text-ink-soft uppercase tracking-wide mb-2">{t('dashboard.kpi.titolo')}</h2>
          <div className="grid md:grid-cols-3 gap-3">
            {blocchiKpi.map((b) => (
              <div key={b.testid} data-testid={b.testid}>
                <StackedBarChart
                  title={b.titolo} color={b.color} unit="€"
                  emptyLabel={t('dashboard.throughput.nessun_dato')}
                  testid={`${b.testid}-bars`}
                  format={eur}
                  data={b.blocco.bucket.map((bk) => ({
                    label: formatPeriodo(bk.periodo, granularita),
                    periodo: bk.periodo,
                    totale: bk.imponibile,
                    segmenti: bk.segmenti.map((sg) => ({
                      chiave: sg.chiave, etichetta: sg.etichetta, valore: sg.imponibile,
                    })),
                  }))}
                />
                <p className="text-[11px] text-ink-soft mt-1" data-testid={`${b.testid}-totale`}>
                  {t('dashboard.kpi.totale_periodo', {
                    imponibile: eur(b.blocco.totale_imponibile), totale: eur(b.blocco.totale_con_iva),
                  })}
                </p>
                {b.nota && <p className="text-[11px] text-ink-faint italic" data-testid={`${b.testid}-nota`}>{b.nota}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 3-ter) Magazzino vendite (V74), SOTTO le KPI amministrazione.
             Cosa c'è DA VENDERE adesso, non cosa è stato prodotto: la
             differenza con l'Andamento qui sopra è che lì un sacco uscito
             conta ancora e qui sparisce. Nessun gate di ruolo: sono kg, non
             euro — il gate delle KPI difende i ricavi. */}
      <MagazzinoVenditeSection />

      {/* 4) Saturazione aree + 5) Scadenze compliance */}
      <section className="grid md:grid-cols-2 gap-6">
        <div>
          <h2 className="text-sm font-semibold text-ink-soft uppercase tracking-wide mb-2">{t('dashboard.saturazione.titolo')}</h2>
          <div className="bg-white border border-stone-mid rounded-xl p-4 space-y-3">
            {areeErr ? (
              <WidgetError onRetry={loadAree} />
            ) : aree.length === 0 ? (
              <div className="text-sm text-ink-soft text-center py-4">{t('dashboard.saturazione.nessuna_area')}</div>
            ) : aree.map((a) => (
              <div key={a.area_id} data-testid={`area-${a.codice}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium text-ink-dark">Area {a.codice} <span className="text-ink-faint font-normal">· {a.tipologia}</span></span>
                  <span className="text-ink-soft">
                    {a.percentuale == null ? t('dashboard.saturazione.capacita_non_impostata') : `${a.percentuale}% · ${kg(a.saldo_kg)} / ${kg(a.capacita_kg)}`}
                  </span>
                </div>
                <div className="h-2.5 rounded-full bg-stone-bg overflow-hidden">
                  {a.percentuale != null && (
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, a.percentuale)}%`, background: satColor(a.percentuale) }} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-ink-soft uppercase tracking-wide mb-2">{t('dashboard.scadenze.titolo')}</h2>
          <div className="bg-white border border-stone-mid rounded-xl divide-y divide-stone-mid">
            {scadenze.map((s, i) => (
              <div key={s.chiave} className={`flex items-center justify-between px-4 py-3 ${i === 0 ? 'bg-sage-light/40' : ''}`}>
                <div className="flex items-center gap-2">
                  <CalendarClock size={15} className={i === 0 ? 'text-sage-dark' : 'text-ink-faint'} />
                  <span className={`text-sm ${i === 0 ? 'font-semibold text-sage-dark' : 'text-ink-dark'}`}>{s.label}</span>
                  {s.nota && <span className="text-[10px] text-ink-faint">({t(s.nota)})</span>}
                </div>
                <span className={`text-sm ${i === 0 ? 'font-semibold text-sage-dark' : 'text-ink-soft'}`}>
                  {s.data.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {drill && (
        <DrillDownTipologieModal
          periodo={drill.periodo}
          granularita={granularita}
          materiale={drill.materiale}
          etichetta={drill.etichetta}
          kgSegmento={drill.kg}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}
